<template>
  <div class="user-avatar" :class="[`user-avatar--${size}`, { clickable }]" :style="containerStyle">
    <img
        v-if="pictureUrl"
        :src="pictureUrl"
        :alt="`${name || email} avatar`"
        class="user-avatar__img"
        @error="onImageError"
    />
    <span v-else class="user-avatar__initials" :style="initialsStyle">{{ initials }}</span>
  </div>
</template>

<script setup lang="ts">
import {computed, ref, onMounted} from 'vue'
import {useProfilePicture} from '@/composables/useProfilePicture'
import {getInitialsFromIdentity} from '@/utils/identity'

const props = withDefaults(defineProps<{
  email?: string
  name?: string
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
  clickable?: boolean
  picture?: string | null
}>(), {
  email: '',
  name: '',
  size: 'md',
  clickable: false,
  picture: undefined,
})

const {getPicture, fetchPictures, pictureCache} = useProfilePicture()
const imgFailed = ref(false)

const sizeMap: Record<string, number> = {
  xs: 24,
  sm: 32,
  md: 40,
  lg: 56,
  xl: 80,
}

const fontSizeMap: Record<string, string> = {
  xs: '0.6rem',
  sm: '0.72rem',
  md: '0.85rem',
  lg: '1.1rem',
  xl: '1.5rem',
}

const initials = computed(() => {
  return getInitialsFromIdentity({name: props.name, email: props.email})
})

const pictureUrl = computed(() => {
  if (imgFailed.value) return null
  // Direct prop takes priority
  if (props.picture !== undefined) return props.picture
  // Then check cache
  if (props.email) return getPicture(props.email)
  return null
})

const containerStyle = computed(() => {
  const px = sizeMap[props.size] || 40
  return {width: `${px}px`, height: `${px}px`}
})

const initialsStyle = computed(() => ({
  fontSize: fontSizeMap[props.size] || '0.85rem',
}))

function onImageError() {
  imgFailed.value = true
}

onMounted(() => {
  // If we have an email and no direct picture prop, try to fetch from cache/API
  if (props.email && props.picture === undefined) {
    const cached = getPicture(props.email)
    if (cached === null && !(props.email.toLowerCase() in pictureCache.value)) {
      fetchPictures([props.email])
    }
  }
})
</script>

<style scoped>
.user-avatar {
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #3b82f6, #8b5cf6);
  position: relative;
}

.user-avatar.clickable {
  cursor: pointer;
  transition: transform 0.15s, box-shadow 0.2s;
}

.user-avatar.clickable:hover {
  transform: scale(1.05);
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.2);
}

.user-avatar__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.user-avatar__initials {
  color: white;
  font-weight: 700;
  letter-spacing: 0.02em;
  user-select: none;
}
</style>
